import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { useAuth } from '../contexts/AuthContext';
import {
  getHubContactStatus,
  createContact,
  PlanterContactType,
  PlanterWithContactStatus,
  formatPlanterLocation,
  effectiveHubContactStatus,
} from '../api/plantersService';
import { assessmentsService } from '../api/assessmentsService';
import { getImageUrl } from '../utils/imageUrl';
import {
  getAdminHubContactOverview,
  adminHubStableId,
  AdminHubOverviewItem,
  AdminHubOverviewPlanter,
} from '../api/adminService';

const parseAssessmentDate = (value: unknown) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const Dashboard: React.FC = () => {
  const contactTypeOptions: Array<{ value: PlanterContactType; label: string }> = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'text', label: 'Text Message' },
    { value: 'email', label: 'Email' },
    { value: 'in-person', label: 'In-Person' },
    { value: 'other', label: 'Other' },
  ];
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [contactModal, setContactModal] = useState<{ planterId: number; planterName: string } | null>(null);
  const [contactType, setContactType] = useState<PlanterContactType>('phone');
  const [contactNotes, setContactNotes] = useState('');
  
  const hasRole = (roleName: string) => (
    user?.roles?.some((role) => (typeof role === 'string' ? role === roleName : role.name === roleName)) || false
  );
  const isHubStaff = hasRole('hub_leader') || hasRole('staff') || hasRole('admin');
  const isAdmin = hasRole('admin');
  const isAssessor = hasRole('assessor');
  const hubInfo = user?.managedHubs?.[0];

  const [expandedAdminHubIds, setExpandedAdminHubIds] = useState<Set<string>>(new Set());

  const contactQuery = useQuery({
    queryKey: ['hub-contact-status'],
    queryFn: getHubContactStatus,
    enabled: isHubStaff,
  });

  const planters: PlanterWithContactStatus[] = contactQuery.data?.data || [];
  const cadenceInfo =
    contactQuery.data?.cadenceInfo ||
    'Cadence depends on each planter’s phase (growth, training, funding, or post-funding).';

  const adminOverviewQuery = useQuery({
    queryKey: ['admin-hub-contact-overview'],
    queryFn: getAdminHubContactOverview,
    enabled: isAdmin,
  });

  const adminOverviewHubs: AdminHubOverviewItem[] = useMemo(
    () => adminOverviewQuery.data?.data || [],
    [adminOverviewQuery.data?.data]
  );
  const adminCadenceInfo =
    adminOverviewQuery.data?.cadenceInfo ||
    'Cadence depends on each planter’s phase (growth, training, funding, or post-funding).';

  const adminOverviewTotals = useMemo(() => {
    let total = 0;
    let contacted = 0;
    let pending = 0;
    let overdue = 0;
    let needsInPerson = 0;
    let exempt = 0;
    for (const hub of adminOverviewHubs) {
      total += hub.summary.total;
      contacted += hub.summary.contacted;
      pending += hub.summary.pending;
      overdue += hub.summary.overdue;
      needsInPerson += hub.summary.needsInPerson ?? 0;
      exempt += hub.summary.exempt ?? 0;
    }
    return { total, contacted, pending, overdue, needsInPerson, exempt };
  }, [adminOverviewHubs]);

  const toggleAdminHub = (stableId: string) => {
    setExpandedAdminHubIds((prev) => {
      const next = new Set(prev);
      if (next.has(stableId)) next.delete(stableId);
      else next.add(stableId);
      return next;
    });
  };

  const markContactedMutation = useMutation({
    mutationFn: async ({ planterId, notes, contactType }: { planterId: number; notes: string; contactType: PlanterContactType }) => {
      return createContact(planterId, {
        contactDate: new Date().toISOString(),
        contactType,
        notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hub-contact-status'] });
      setContactModal(null);
      setContactType('phone');
      setContactNotes('');
    },
  });

  const { data: assessmentsData } = useQuery({
    queryKey: ['assessments'],
    queryFn: () => assessmentsService.list(),
    enabled: isAssessor,
  });

  const nextAssessment = useMemo(() => {
    const items = assessmentsData?.data || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return items
      .filter((assessment) => {
        const date = parseAssessmentDate(assessment.date);
        if (!date) return false;
        return date >= today;
      })
      .sort((a, b) => {
        const aDate = parseAssessmentDate(a.date);
        const bDate = parseAssessmentDate(b.date);
        return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
      })[0];
  }, [assessmentsData?.data]);

  const { data: nextAssessmentDetail } = useQuery({
    queryKey: ['assessments', nextAssessment?.id, 'detail'],
    queryFn: () => assessmentsService.get(nextAssessment.id),
    enabled: isAssessor && Boolean(nextAssessment?.id),
  });

  const assignedInterviewRoleIds = useMemo(() => {
    const eventRoles = nextAssessmentDetail?.data?.eventRoles || [];
    return eventRoles
      .filter((role) => (
        role.assignedUserId === user?.id
        || role.assistantUserId === user?.id
        || role.teamAssignments?.some((assignment) => (
          assignment.assignedUserId === user?.id || assignment.assistantUserId === user?.id
        ))
      ) && role.role?.roleType === 'assessor' && role.role?.isInterviewArea)
      .map((role) => role.roleId);
  }, [nextAssessmentDetail?.data?.eventRoles, user?.id]);

  const assignedRoleLabel = useMemo(() => {
    const eventRoles = nextAssessmentDetail?.data?.eventRoles || [];
    const assignedRoles = eventRoles.filter((role) => (
      role.assignedUserId === user?.id
      || role.assistantUserId === user?.id
      || role.teamAssignments?.some((assignment) => (
        assignment.assignedUserId === user?.id || assignment.assistantUserId === user?.id
      ))
    ));
    if (!assignedRoles.length) {
      return 'No Role Assigned';
    }
    return assignedRoles
      .map((role) => {
        const isPrimary = role.assignedUserId === user?.id
          || role.teamAssignments?.some((assignment) => assignment.assignedUserId === user?.id);
        const isAssistant = !isPrimary && (
          role.assistantUserId === user?.id
          || role.teamAssignments?.some((assignment) => assignment.assistantUserId === user?.id)
        );
        const teams = role.teamAssignments
          ?.filter((assignment) => (
            assignment.assignedUserId === user?.id || assignment.assistantUserId === user?.id
          ))
          .map((assignment) => `Team ${assignment.teamKey}`) || [];
        const label = role.role?.displayName || role.role?.name || role.assignedName || 'Assigned Role';
        const assistingTag = isAssistant ? ' (Assisting)' : '';
        return teams.length ? `${label}${assistingTag} (${teams.join(', ')})` : `${label}${assistingTag}`;
      })
      .join(', ');
  }, [nextAssessmentDetail?.data?.eventRoles, user?.id]);

  const interviewMatrixQuery = useQuery({
    queryKey: ['assessments', nextAssessment?.id, 'interview-matrix'],
    queryFn: () => assessmentsService.getInterviewMatrix(nextAssessment.id),
    enabled: isAssessor && Boolean(nextAssessment?.id) && assignedInterviewRoleIds.length > 0,
  });

  const masterScheduleQuery = useQuery({
    queryKey: ['assessments', nextAssessment?.id, 'master-schedule'],
    queryFn: () => assessmentsService.getMasterSchedule(nextAssessment.id),
    enabled: isAssessor && Boolean(nextAssessment?.id),
  });

  const reportAssignmentsQuery = useQuery({
    queryKey: ['assessments', nextAssessment?.id, 'report-assignments'],
    queryFn: () => assessmentsService.listReportAssignments(nextAssessment.id),
    enabled: isAssessor && Boolean(nextAssessment?.id) && assignedInterviewRoleIds.length > 0,
  });

  const displayCandidateName = (c: any) =>
    c?.name || [c?.firstName, c?.lastName].filter(Boolean).join(' ') || '—';

  const displaySpouseName = (c: any) =>
    c?.spouseName || [c?.wifeFirstName, c?.wifeLastName].filter(Boolean).join(' ') || 'Spouse';

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Here's what matters most today.
          </p>
        </div>

        {(!isHubStaff && !isAssessor) && (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Welcome to 242 Network
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                You're logged in as {user?.email}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {isHubStaff && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="min-w-0">
                    <h3 className="text-lg font-medium text-gray-900">
                      {hubInfo?.name ? hubInfo.name : 'No Hub Assigned'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{cadenceInfo}</p>
                    {!hubInfo?.name ? (
                      <p className="text-sm text-gray-500 mt-1">You are not currently assigned to a hub in your profile.</p>
                    ) : null}
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-3">
                    <Link
                      to="/map"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Map
                    </Link>
                    <Link
                      to="/planters"
                      className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap"
                    >
                      View all
                    </Link>
                  </div>
                </div>
                {contactQuery.isPending && !contactQuery.data ? (
                  <div className="py-8 text-center text-sm text-gray-600">Loading contact status…</div>
                ) : contactQuery.isError && !contactQuery.data ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                    <p className="text-sm text-amber-900">Failed to load contact status.</p>
                    <button
                      type="button"
                      onClick={() => contactQuery.refetch()}
                      className="mt-2 text-sm font-medium text-primary-700 hover:underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : planters && planters.length > 0 ? (
                  <div className="space-y-3">
                    {planters.map((planter) => {
                      const cs = effectiveHubContactStatus(planter);
                      const headshotUrl = planter.headshotImagePath
                        ? getImageUrl(planter.headshotImagePath, { width: 80, quality: 70, format: 'webp' })
                        : undefined;
                      return (
                        <div
                          key={planter.id}
                          className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                            cs === 'contacted'
                              ? 'bg-green-50 border border-green-200'
                              : cs === 'overdue'
                                ? 'bg-red-50 border border-red-200'
                                : cs === 'needs_in_person'
                                  ? 'bg-amber-50 border border-amber-200'
                                  : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div
                            className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer"
                            onClick={() => navigate(`/planters/${planter.id}`)}
                          >
                            <Avatar name={planter.name} size="sm" imageUrl={headshotUrl} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {planter.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {formatPlanterLocation(planter) || 'No city / state'}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {planter.lastContact
                                  ? `Last contact: ${new Date(planter.lastContact.contactDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                  : 'No contact yet'}
                              </p>
                            </div>
                          </div>
                          <div className="ml-3 flex items-center gap-2 flex-shrink-0">
                            <Link
                              to={`/planters/${planter.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-gray-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                            >
                              View history
                            </Link>
                            {cs === 'contacted' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                                Contacted
                              </span>
                            ) : cs !== 'exempt' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContactModal({ planterId: planter.id, planterName: planter.name });
                                  setContactType(cs === 'needs_in_person' ? 'in-person' : 'phone');
                                }}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors min-h-touch ${
                                  cs === 'overdue'
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : cs === 'needs_in_person'
                                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                                      : 'bg-primary-600 text-white hover:bg-primary-700'
                                }`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {cs === 'overdue' ? 'Overdue' : cs === 'needs_in_person' ? 'Log in-person' : 'Mark Contacted'}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">
                      No planters assigned to this hub yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {isAssessor && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Next Assessment</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Your next scheduled assessment event.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M3 11h18M5 7h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
                    </svg>
                  </div>
                </div>
                {nextAssessment ? (
                  <Link
                    to={`/assessments/${nextAssessment.id}`}
                    className="mt-4 block rounded-lg border border-gray-200 p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{nextAssessment.name}</div>
                        <div className="text-sm text-gray-600">
                          {(parseAssessmentDate(nextAssessment.date) || new Date(nextAssessment.date)).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          {nextAssessment.location ? ` • ${nextAssessment.location}` : ''}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {assignedRoleLabel}
                        </div>
                      </div>
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                ) : (
                  <div className="mt-4 rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                    No upcoming assessments found.
                  </div>
                )}

                {nextAssessment && assignedInterviewRoleIds.length > 0 && (
                  <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                      <div className="text-sm font-semibold text-gray-900">Your Interview Schedule</div>
                      <div className="text-xs text-gray-600">Only your assigned interview area(s).</div>
                    </div>
                    {interviewMatrixQuery.isLoading ? (
                      <div className="p-4 text-sm text-gray-600">Loading schedule…</div>
                    ) : interviewMatrixQuery.isError ? (
                      <div className="p-4 text-sm text-red-700">Failed to load schedule.</div>
                    ) : (
                      (() => {
                        const matrix = interviewMatrixQuery.data?.data;
                        const roles = (matrix?.roles || [])
                          .filter((r: any) => assignedInterviewRoleIds.includes(r.id))
                          .slice()
                          .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                        const defaultScheduleRows: Array<{ time: string; label?: string; interviewPeriodIndex?: number; isDivider?: boolean }> = [
                          { time: '10:30 AM', interviewPeriodIndex: 1 },
                          { time: '11:20 AM', label: 'Break', isDivider: true },
                          { time: '11:30 AM', interviewPeriodIndex: 2 },
                          { time: '12:30 PM', label: 'Lunch', isDivider: true },
                          { time: '1:15 PM', interviewPeriodIndex: 3 },
                          { time: '2:05 PM', label: 'Break', isDivider: true },
                          { time: '2:15 PM', interviewPeriodIndex: 4 },
                          { time: '3:05 PM', label: 'Break', isDivider: true },
                          { time: '3:15 PM', interviewPeriodIndex: 5 },
                          { time: '4:05 PM', label: 'Break', isDivider: true },
                          { time: '4:15 PM', interviewPeriodIndex: 6 },
                        ];
                        const masterItems = (masterScheduleQuery.data?.data?.days || [])
                          .flatMap((day) => day.items)
                          .slice()
                          .sort((a, b) => {
                            if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
                            return String(a.startTime).localeCompare(String(b.startTime));
                          });
                        const hasMasterInterviewRows = masterItems.some((item) => item.slotType === 'interview' && item.interviewPeriodIndex);
                        const scheduleRows: Array<{ time: string; label?: string; interviewPeriodIndex?: number; isDivider?: boolean }> = hasMasterInterviewRows
                          ? masterItems.map((item) => (
                            item.slotType === 'interview'
                              ? {
                                time: item.startTimeLabel || String(item.startTime).slice(0, 5),
                                interviewPeriodIndex: item.interviewPeriodIndex || undefined,
                              }
                              : {
                                time: item.startTimeLabel || String(item.startTime).slice(0, 5),
                                label: item.label,
                                isDivider: true,
                              }
                          ))
                          : defaultScheduleRows;

                        const cellByPeriodRoleId = new Map<string, any>();
                        for (const c of matrix?.cells || []) {
                          cellByPeriodRoleId.set(`${c.interviewPeriodIndex}:${c.roleId}`, c);
                        }

                        const reportAssignmentByKey = new Map<string, any>();
                        for (const assignment of reportAssignmentsQuery.data?.data || []) {
                          if (!assignment.id) continue;
                          reportAssignmentByKey.set(`${assignment.participantId}:${assignment.roleId}`, assignment);
                        }

                        const renderCell = (row: any, role: any) => {
                          if (!row.interviewPeriodIndex) return <span className="text-gray-400">—</span>;
                          const cell = cellByPeriodRoleId.get(`${row.interviewPeriodIndex}:${role.id}`);
                          const pc = (cell?.participant as any)?.portalCandidate;
                          if (!pc) return <span className="text-gray-400">—</span>;
                          const participantId = (cell?.participant as any)?.id;
                          const reportAssignment = participantId ? reportAssignmentByKey.get(`${participantId}:${role.id}`) : null;
                          const reportHref = reportAssignment ? `/assessments/${nextAssessment?.id}/reports/${reportAssignment.id}` : null;
                          const portalCandidateId = pc?.id;

                          const headshotUrl = getImageUrl(pc?.headshotImagePath, { width: 160, quality: 70, format: 'webp' });
                          const candidateName = displayCandidateName(pc);
                          const spouseName = displaySpouseName(pc);

                          if (role.interviewAudience === 'spouse_only') {
                            return (
                              <div className="flex items-center gap-4">
                                <Avatar name={spouseName} size="lg" imageUrl={headshotUrl} />
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm font-semibold text-gray-900">{spouseName}</span>
                                  <div className="flex items-center gap-3">
                                    {reportHref ? (
                                      <Link to={reportHref} className="text-xs font-medium text-primary-700 hover:underline">
                                        Write report
                                      </Link>
                                    ) : null}
                                    <Link
                                      to={`/assessments/${nextAssessment?.id}/candidates/${portalCandidateId}`}
                                      className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline"
                                    >
                                      View details
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (role.interviewAudience === 'candidate_only') {
                            return (
                              <div className="flex items-center gap-4">
                                <Avatar name={candidateName} size="lg" imageUrl={headshotUrl} />
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm font-semibold text-gray-900">{candidateName}</span>
                                  <div className="flex items-center gap-3">
                                    {reportHref ? (
                                      <Link to={reportHref} className="text-xs font-medium text-primary-700 hover:underline">
                                        Write report
                                      </Link>
                                    ) : null}
                                    <Link
                                      to={`/assessments/${nextAssessment?.id}/candidates/${portalCandidateId}`}
                                      className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline"
                                    >
                                      View details
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center gap-4">
                              <Avatar name={candidateName} size="lg" imageUrl={headshotUrl} />
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-semibold text-gray-900">
                                  {candidateName}
                                  {spouseName ? <span className="text-gray-500"> &amp; {spouseName}</span> : null}
                                </span>
                                <div className="flex items-center gap-3">
                                  {reportHref ? (
                                    <Link to={reportHref} className="text-xs font-medium text-primary-700 hover:underline">
                                      Write report
                                    </Link>
                                  ) : null}
                                  <Link
                                    to={`/assessments/${nextAssessment?.id}/candidates/${portalCandidateId}`}
                                    className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline"
                                  >
                                    View details
                                  </Link>
                                </div>
                              </div>
                            </div>
                          );
                        };

                        return (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                                    Time
                                  </th>
                                  {roles.map((r: any) => (
                                    <th
                                      key={r.id}
                                      className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
                                    >
                                      {r.displayName || r.name}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {scheduleRows.map((row) => {
                                  if (row.isDivider) {
                                    return (
                                      <tr key={`${row.time}-${row.label}`} className="bg-gray-50">
                                        <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                          <span className="font-medium">{row.time}</span>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-700 text-center font-medium" colSpan={roles.length}>
                                          {row.label}
                                        </td>
                                      </tr>
                                    );
                                  }

                                  return (
                                    <tr key={`${row.time}-${row.interviewPeriodIndex}`} className="hover:bg-gray-50">
                                      <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                        <span className="font-medium">{row.time}</span>
                                      </td>
                                      {roles.map((r: any) => (
                                        <td key={r.id} className="px-4 py-2 text-sm text-gray-900">
                                          {renderCell(row, r)}
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                })}
                                {roles.length === 0 && (
                                  <tr>
                                    <td className="px-4 py-4 text-sm text-gray-600" colSpan={2}>
                                      No interview roles assigned.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}

                {nextAssessment && (masterScheduleQuery.data?.data?.days || []).some((d) => d.items.length > 0) && (
                  <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Day Schedule</div>
                        <div className="text-xs text-gray-600">Whole-day agenda for the next assessment.</div>
                      </div>
                      <Link
                        to={`/assessments/${nextAssessment.id}`}
                        className="text-xs font-medium text-primary-700 hover:underline"
                      >
                        Open assessment
                      </Link>
                    </div>
                    <div className="divide-y divide-gray-200 max-h-72 overflow-y-auto">
                      {(masterScheduleQuery.data?.data?.days || []).flatMap((day) =>
                        day.items.map((item) => (
                          <div
                            key={`${day.dayIndex}-${item.representativeSlotId}`}
                            className="flex items-center justify-between gap-3 px-4 py-2"
                          >
                            <div className="min-w-0">
                              <div className="text-sm text-gray-900">
                                <span className="font-medium">
                                  {item.startTimeLabel || String(item.startTime).slice(0, 5)}
                                </span>
                                <span className="ml-2 text-gray-700">{item.label}</span>
                              </div>
                              {day.date ? (
                                <div className="text-xs text-gray-500">Day {day.dayIndex + 1} • {day.date}</div>
                              ) : null}
                            </div>
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">{item.slotType}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {nextAssessment && (
                  <div className="mt-4">
                    <Link
                      to={`/assessments/${nextAssessment.id}/after-party`}
                      className="inline-flex items-center justify-center rounded-md border border-primary-200 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                    >
                      View After Party Reports
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="rounded-2xl bg-gray-100 border border-gray-200 overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900">Hub Contact Overview</h3>
                <p className="text-sm text-gray-600 mt-1">Contact compliance across hubs</p>
                <p className="text-xs text-gray-500 mt-1">{adminCadenceInfo}</p>

                {adminOverviewQuery.isPending && !adminOverviewQuery.data ? (
                  <div className="py-8 text-center text-sm text-gray-600">Loading overview…</div>
                ) : adminOverviewQuery.isError && !adminOverviewQuery.data ? (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                    <p className="text-sm text-amber-900">Failed to load hub overview.</p>
                    <button
                      type="button"
                      onClick={() => adminOverviewQuery.refetch()}
                      className="mt-2 text-sm font-medium text-primary-700 hover:underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : adminOverviewHubs.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">No active hubs found.</p>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                      <div className="rounded-lg bg-white p-3 text-center border border-gray-200">
                        <div className="text-xl font-bold text-gray-900">{adminOverviewTotals.total}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                      <div className="rounded-lg bg-green-50 p-3 text-center border border-green-200">
                        <div className="text-xl font-bold text-green-800">{adminOverviewTotals.contacted}</div>
                        <div className="text-xs text-gray-600">Contacted</div>
                      </div>
                      <div className="rounded-lg bg-orange-50 p-3 text-center border border-orange-200">
                        <div className="text-xl font-bold text-orange-800">{adminOverviewTotals.pending}</div>
                        <div className="text-xs text-gray-600">Pending</div>
                      </div>
                      <div className="rounded-lg bg-amber-50 p-3 text-center border border-amber-200">
                        <div className="text-xl font-bold text-amber-900">{adminOverviewTotals.needsInPerson}</div>
                        <div className="text-xs text-gray-600">In-person due</div>
                      </div>
                      <div className="rounded-lg bg-red-50 p-3 text-center border border-red-200">
                        <div className="text-xl font-bold text-red-800">{adminOverviewTotals.overdue}</div>
                        <div className="text-xs text-gray-600">Overdue</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3 text-center border border-slate-200">
                        <div className="text-xl font-bold text-slate-800">{adminOverviewTotals.exempt}</div>
                        <div className="text-xs text-gray-600">Tracking off</div>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      {adminOverviewHubs.map((hub) => {
                        const sid = adminHubStableId(hub);
                        const expanded = expandedAdminHubIds.has(sid);
                        const { summary } = hub;
                        const pct =
                          summary.total > 0 ? Math.round((summary.contacted / summary.total) * 100) : 0;
                        return (
                          <div key={sid} className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => toggleAdminHub(sid)}
                              className="w-full text-left px-3 py-3 flex flex-wrap items-center gap-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 min-w-[140px]">
                                <div className="text-sm font-semibold text-gray-900">{hub.name}</div>
                                <div className="text-xs text-gray-500">
                                  {hub.hubLeader
                                    ? `${hub.hubLeader.firstName} ${hub.hubLeader.lastName}`
                                    : 'No hub leader assigned'}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                                <span className="flex items-center gap-0.5">
                                  <span className="h-2 w-2 rounded-full bg-green-500" />
                                  {summary.contacted}
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <span className="h-2 w-2 rounded-full bg-orange-400" />
                                  {summary.pending}
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                                  {summary.needsInPerson ?? 0}
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <span className="h-2 w-2 rounded-full bg-red-500" />
                                  {summary.overdue}
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <span className="h-2 w-2 rounded-full bg-slate-400" />
                                  {summary.exempt ?? 0}
                                </span>
                              </div>
                              <div className="flex flex-col items-end gap-1 min-w-[72px]">
                                <span className="text-xs text-gray-500">{pct}%</span>
                                <span className="text-xs font-medium text-gray-700">
                                  {summary.contacted}/{summary.total}
                                </span>
                                <div className="h-1.5 w-[72px] rounded-full bg-gray-200 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-orange-400' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                              <svg
                                className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {expanded && (
                              <div className="border-t border-gray-100 px-2 py-2 bg-gray-50/80">
                                {hub.planters.length === 0 ? (
                                  <p className="text-xs text-gray-500 px-2 py-2">No planters assigned to this hub.</p>
                                ) : (
                                  hub.planters.map((planter) => (
                                    <AdminOverviewPlanterRow key={planter.id} planter={planter} />
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Mark Contacted Modal */}
          {contactModal && (
            <div className="fixed z-50 inset-0 overflow-y-auto">
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => { setContactModal(null); setContactType('phone'); setContactNotes(''); }}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-1">
                      Mark Contacted
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Record contact with <span className="font-medium text-gray-700">{contactModal.planterName}</span>
                    </p>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                      <select
                        value={contactType}
                        onChange={(e) => setContactType(e.target.value as PlanterContactType)}
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      >
                        {contactTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        rows={4}
                        value={contactNotes}
                        onChange={(e) => setContactNotes(e.target.value)}
                        placeholder="How is this planter doing? Any prayer requests, concerns, or updates..."
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      />
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                    <button
                      type="button"
                      disabled={markContactedMutation.isPending}
                      onClick={() => markContactedMutation.mutate({ planterId: contactModal.planterId, notes: contactNotes, contactType })}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {markContactedMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setContactModal(null); setContactType('phone'); setContactNotes(''); }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

function AdminOverviewPlanterRow({ planter }: { planter: AdminHubOverviewPlanter }) {
  const cs = effectiveHubContactStatus(planter);
  const dotClass =
    cs === 'contacted'
      ? 'bg-green-500'
      : cs === 'exempt'
        ? 'bg-slate-400'
        : cs === 'overdue'
          ? 'bg-red-500'
          : cs === 'needs_in_person'
            ? 'bg-amber-500'
            : 'bg-orange-400';
  const loc = [planter.city, planter.state].filter(Boolean).join(', ');
  const headshotUrl = planter.headshotImagePath
    ? getImageUrl(planter.headshotImagePath, { width: 80, quality: 70, format: 'webp' })
    : undefined;
  return (
    <Link
      to={`/planters/${planter.id}`}
      className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white transition-colors"
    >
      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotClass}`} />
      <Avatar name={planter.name} size="sm" imageUrl={headshotUrl} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">
          {planter.name}
          {planter.spouseName ? (
            <span className="text-gray-500 font-normal"> &amp; {planter.spouseName}</span>
          ) : null}
        </div>
        <div className="text-xs text-gray-500 truncate">{loc || '—'}</div>
        {planter.lastContact ? (
          <div className="text-xs text-gray-400">
            Last: {new Date(planter.lastContact.contactDate).toLocaleDateString()}
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export default Dashboard;
